import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Footer from './Footer';

const MotorLifeCalculator = () => {
  const backendUrl = process.env.REACT_APP_BACKEND_URL;
  
  // Authentication state
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [authStep, setAuthStep] = useState('email'); // 'email', 'code', 'authenticated'
  const [email, setEmail] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [authError, setAuthError] = useState('');
  const [authLoading, setAuthLoading] = useState(false);
  const [checkingSession, setCheckingSession] = useState(true);
  
  // State for all dropdown selections
  const [motorBrand, setMotorBrand] = useState('');
  const [horsepower, setHorsepower] = useState('');
  const [dutyCycle, setDutyCycle] = useState('');
  const [operatingTime, setOperatingTime] = useState('');
  const [ratedLife, setRatedLife] = useState('');
  const [actualHours, setActualHours] = useState('');
  const [remainingLife, setRemainingLife] = useState(null);
  const [estimatedLifeExpectancy, setEstimatedLifeExpectancy] = useState(null);

  // Motor brands (comprehensive list)
  const motorBrands = [
    'ABB', 'AEG', 'Allen-Bradley', 'Baldor', 'Bosch', 'Brook Crompton',
    'Dayton', 'Emerson', 'General Electric (GE)', 'Groschopp', 'Hitachi',
    'Honeywell', 'Hyundai', 'Leeson', 'Leroy-Somer', 'Lincoln Electric',
    'Marathon', 'Mitsubishi', 'Nidec', 'Nord', 'Regal Rexnord', 'Reliance',
    'SEW-Eurodrive', 'Siemens', 'Teco-Westinghouse', 'Toshiba', 'US Motors',
    'WEG', 'Westinghouse', 'Other'
  ];

  // Horsepower sizes (comprehensive range)
  const horsepowerSizes = [
    '1/8', '1/6', '1/4', '1/3', '1/2', '3/4', '1', '1.5', '2', '3', '5',
    '7.5', '10', '15', '20', '25', '30', '40', '50', '60', '75', '100',
    '125', '150', '200', '250', '300', '350', '400', '450', '500', '600',
    '700', '800', '900', '1000'
  ];

  // Duty cycle percentages (0% to 100% in 5% increments)
  const dutyCycles = Array.from({ length: 21 }, (_, i) => `${i * 5}%`);

  // Operating time (0:00 to 24:00 in 0:30 increments)
  const operatingTimes = [];
  for (let hour = 0; hour <= 24; hour++) {
    for (let min = 0; min < 60; min += 30) {
      if (hour === 24 && min > 0) break;
      const time = `${String(hour).padStart(2, '0')}:${String(min).padStart(2, '0')}`;
      operatingTimes.push(time);
    }
  }

  // Rated life options (0 to 100,000 hrs in 5,000 hr increments)
  const ratedLifeOptions = Array.from({ length: 21 }, (_, i) => i * 5000);

  // Actual hours (0 to 100,000 hrs in 1,000 hr increments)
  const actualHoursOptions = Array.from({ length: 101 }, (_, i) => i * 1000);

  // Check for existing session on mount
  useEffect(() => {
    const checkSession = async () => {
      const storedToken = localStorage.getItem('maintenance_token');
      if (storedToken) {
        try {
          const response = await fetch(`${backendUrl}/api/maintenance/validate-session`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ token: storedToken })
          });
          
          if (response.ok) {
            setIsAuthenticated(true);
            setAuthStep('authenticated');
          } else {
            localStorage.removeItem('maintenance_token');
          }
        } catch (error) {
          console.error('Session validation error:', error);
          localStorage.removeItem('maintenance_token');
        }
      }
      setCheckingSession(false);
    };
    
    checkSession();
  }, [backendUrl]);

  // Calculate Estimated Life Expectancy: EL = (RL × DC) / 100
  useEffect(() => {
    if (ratedLife !== '' && dutyCycle !== '') {
      const rl = parseInt(ratedLife);
      const dc = parseInt(dutyCycle.replace('%', ''));
      if (dc > 0) {
        const el = (rl * dc) / 100;
        setEstimatedLifeExpectancy(el);
      } else {
        setEstimatedLifeExpectancy(0);
      }
    } else {
      setEstimatedLifeExpectancy(null);
    }
  }, [ratedLife, dutyCycle]);

  // Calculate remaining life: RLH = EL - AH
  useEffect(() => {
    if (estimatedLifeExpectancy !== null && actualHours !== '') {
      const ah = parseInt(actualHours);
      const rlh = estimatedLifeExpectancy - ah;
      setRemainingLife(rlh);
    } else {
      setRemainingLife(null);
    }
  }, [estimatedLifeExpectancy, actualHours]);

  const handleRequestCode = async (e) => {
    e.preventDefault();
    setAuthError('');
    setAuthLoading(true);
    
    try {
      const response = await fetch(`${backendUrl}/api/maintenance/request-access`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.toLowerCase().trim() })
      });
      
      const data = await response.json();
      
      if (response.ok) {
        setAuthStep('code');
      } else {
        setAuthError(data.detail || 'Failed to send verification code');
      }
    } catch (error) {
      setAuthError('Network error. Please try again.');
    } finally {
      setAuthLoading(false);
    }
  };

  const handleVerifyCode = async (e) => {
    e.preventDefault();
    setAuthError('');
    setAuthLoading(true);
    
    try {
      const response = await fetch(`${backendUrl}/api/maintenance/verify-access`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          email: email.toLowerCase().trim(),
          code: verificationCode.trim()
        })
      });
      
      const data = await response.json();
      
      if (response.ok) {
        localStorage.setItem('maintenance_token', data.token);
        setIsAuthenticated(true);
        setAuthStep('authenticated');
      } else {
        setAuthError(data.detail || 'Invalid verification code');
      }
    } catch (error) {
      setAuthError('Network error. Please try again.');
    } finally {
      setAuthLoading(false);
    }
  };

  // Show loading state while checking session
  if (checkingSession) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-indigo-900 flex items-center justify-center">
        <div className="text-white text-xl">Loading...</div>
      </div>
    );
  }

  // Show authentication gate if not authenticated
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-indigo-900">
        {/* Header */}
        <div className="bg-black/30 backdrop-blur-md border-b border-white/10">
          <div className="container mx-auto px-4 py-4 flex items-center justify-between">
            <Link to="/" className="flex items-center text-white hover:text-blue-300 transition-colors">
              <svg className="w-6 h-6 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              <span className="font-medium">Back to Home</span>
            </Link>
            <h1 className="text-xl md:text-2xl font-bold text-white">Maintenance Prevention</h1>
            <div className="w-24"></div>
          </div>
        </div>

        {/* Authentication Form */}
        <div className="container mx-auto px-4 py-12 flex items-center justify-center min-h-[70vh]">
          <div className="max-w-md w-full bg-white/10 backdrop-blur-lg rounded-2xl shadow-2xl p-8 border border-white/20">
            <div className="text-center mb-8">
              <div className="w-20 h-20 bg-orange-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-10 h-10 text-orange-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
              </div>
              <h2 className="text-2xl font-bold text-white mb-2">Restricted Access</h2>
              <p className="text-blue-200">
                {authStep === 'email' 
                  ? 'Enter your authorized email to receive a verification code'
                  : 'Enter the verification code sent to your email'}
              </p>
            </div>

            {authStep === 'email' ? (
              <form onSubmit={handleRequestCode}>
                <div className="mb-6">
                  <label className="block text-white font-semibold mb-2">Email Address</label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Enter your authorized email"
                    className="w-full px-4 py-3 bg-white/20 backdrop-blur-sm border border-white/30 rounded-lg text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                    required
                    data-testid="maintenance-email-input"
                  />
                </div>
                
                {authError && (
                  <div className="mb-4 p-3 bg-red-500/20 border border-red-500/50 rounded-lg text-red-200 text-sm" data-testid="auth-error">
                    {authError}
                  </div>
                )}
                
                <button
                  type="submit"
                  disabled={authLoading}
                  className="w-full py-3 bg-gradient-to-r from-orange-500 to-red-500 text-white font-bold rounded-lg hover:from-orange-600 hover:to-red-600 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid="request-code-btn"
                >
                  {authLoading ? 'Sending...' : 'Request Access Code'}
                </button>
              </form>
            ) : (
              <form onSubmit={handleVerifyCode}>
                <div className="mb-4">
                  <p className="text-sm text-blue-200 mb-4">
                    A verification code has been sent to <strong className="text-white">{email}</strong>
                  </p>
                </div>
                
                <div className="mb-6">
                  <label className="block text-white font-semibold mb-2">Verification Code</label>
                  <input
                    type="text"
                    value={verificationCode}
                    onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    placeholder="Enter 6-digit code"
                    className="w-full px-4 py-3 bg-white/20 backdrop-blur-sm border border-white/30 rounded-lg text-white text-center text-2xl tracking-widest placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                    maxLength={6}
                    required
                    data-testid="verification-code-input"
                  />
                </div>
                
                {authError && (
                  <div className="mb-4 p-3 bg-red-500/20 border border-red-500/50 rounded-lg text-red-200 text-sm" data-testid="auth-error">
                    {authError}
                  </div>
                )}
                
                <button
                  type="submit"
                  disabled={authLoading || verificationCode.length !== 6}
                  className="w-full py-3 bg-gradient-to-r from-orange-500 to-red-500 text-white font-bold rounded-lg hover:from-orange-600 hover:to-red-600 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid="verify-code-btn"
                >
                  {authLoading ? 'Verifying...' : 'Verify & Access'}
                </button>
                
                <button
                  type="button"
                  onClick={() => {
                    setAuthStep('email');
                    setVerificationCode('');
                    setAuthError('');
                  }}
                  className="w-full mt-3 py-2 text-blue-300 hover:text-white transition-colors text-sm"
                >
                  ← Use a different email
                </button>
              </form>
            )}
            
            <div className="mt-6 pt-6 border-t border-white/20 text-center">
              <p className="text-xs text-blue-200/70">
                This feature is restricted to authorized personnel only.
              </p>
            </div>
          </div>
        </div>

        <Footer />
      </div>
    );
  }

  // Main Calculator (authenticated view)
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-indigo-900">
      {/* Header */}
      <div className="bg-black/30 backdrop-blur-md border-b border-white/10">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center text-white hover:text-blue-300 transition-colors">
            <svg className="w-6 h-6 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            <span className="font-medium">Back to Home</span>
          </Link>
          <h1 className="text-xl md:text-2xl font-bold text-white">Maintenance Prevention</h1>
          <button
            onClick={() => {
              localStorage.removeItem('maintenance_token');
              setIsAuthenticated(false);
              setAuthStep('email');
              setEmail('');
              setVerificationCode('');
            }}
            className="text-sm text-blue-300 hover:text-white transition-colors"
            data-testid="logout-btn"
          >
            Sign Out
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div className="container mx-auto px-4 py-12">
        {/* Title Section */}
        <div className="text-center mb-12">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
            Motor Life Rate Calculator
          </h2>
          <p className="text-xl text-blue-200 max-w-3xl mx-auto">
            Calculate the remaining operational life of your motors based on rated life, duty cycle, and actual usage hours
          </p>
        </div>

        {/* Calculator Form */}
        <div className="max-w-4xl mx-auto bg-white/10 backdrop-blur-lg rounded-2xl shadow-2xl p-8 border border-white/20">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            
            {/* Dropdown 1: Motor Brand */}
            <div>
              <label className="block text-white font-semibold mb-2">
                Motor Brand
              </label>
              <select
                value={motorBrand}
                onChange={(e) => setMotorBrand(e.target.value)}
                className="w-full px-4 py-3 bg-white/20 backdrop-blur-sm border border-white/30 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                data-testid="motor-brand-select"
              >
                <option value="" className="bg-gray-800">Select Brand</option>
                {motorBrands.map((brand) => (
                  <option key={brand} value={brand} className="bg-gray-800">
                    {brand}
                  </option>
                ))}
              </select>
            </div>

            {/* Dropdown 2: Horsepower */}
            <div>
              <label className="block text-white font-semibold mb-2">
                Motor Size (HP)
              </label>
              <select
                value={horsepower}
                onChange={(e) => setHorsepower(e.target.value)}
                className="w-full px-4 py-3 bg-white/20 backdrop-blur-sm border border-white/30 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                data-testid="horsepower-select"
              >
                <option value="" className="bg-gray-800">Select HP</option>
                {horsepowerSizes.map((hp) => (
                  <option key={hp} value={hp} className="bg-gray-800">
                    {hp} HP
                  </option>
                ))}
              </select>
            </div>

            {/* Dropdown 3: Duty Cycle (DC) */}
            <div>
              <label className="block text-white font-semibold mb-2">
                Duty Cycle (DC)
              </label>
              <select
                value={dutyCycle}
                onChange={(e) => setDutyCycle(e.target.value)}
                className="w-full px-4 py-3 bg-white/20 backdrop-blur-sm border border-white/30 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                data-testid="duty-cycle-select"
              >
                <option value="" className="bg-gray-800">Select Duty Cycle</option>
                {dutyCycles.map((dc) => (
                  <option key={dc} value={dc} className="bg-gray-800">
                    {dc}
                  </option>
                ))}
              </select>
            </div>

            {/* Dropdown 4: Operating Time */}
            <div>
              <label className="block text-white font-semibold mb-2">
                Daily Operating Time
              </label>
              <select
                value={operatingTime}
                onChange={(e) => setOperatingTime(e.target.value)}
                className="w-full px-4 py-3 bg-white/20 backdrop-blur-sm border border-white/30 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                data-testid="operating-time-select"
              >
                <option value="" className="bg-gray-800">Select Time</option>
                {operatingTimes.map((time) => (
                  <option key={time} value={time} className="bg-gray-800">
                    {time}
                  </option>
                ))}
              </select>
            </div>

            {/* Dropdown 5: Rated Life (RL) */}
            <div>
              <label className="block text-white font-semibold mb-2">
                Rated Life (RL)
              </label>
              <select
                value={ratedLife}
                onChange={(e) => setRatedLife(e.target.value)}
                className="w-full px-4 py-3 bg-white/20 backdrop-blur-sm border border-white/30 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                data-testid="rated-life-select"
              >
                <option value="" className="bg-gray-800">Select Rated Life</option>
                {ratedLifeOptions.map((hours) => (
                  <option key={hours} value={hours} className="bg-gray-800">
                    {hours.toLocaleString()} hours
                  </option>
                ))}
              </select>
            </div>

            {/* Dropdown 6: Actual Hours (AH) */}
            <div>
              <label className="block text-white font-semibold mb-2">
                Actual Hours (AH)
              </label>
              <select
                value={actualHours}
                onChange={(e) => setActualHours(e.target.value)}
                className="w-full px-4 py-3 bg-white/20 backdrop-blur-sm border border-white/30 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                data-testid="actual-hours-select"
              >
                <option value="" className="bg-gray-800">Select Actual Hours</option>
                {actualHoursOptions.map((hours) => (
                  <option key={hours} value={hours} className="bg-gray-800">
                    {hours.toLocaleString()} hours
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Estimated Life Expectancy Result */}
          <div className="mt-8 pt-8 border-t border-white/20">
            <label className="block text-white font-semibold mb-3 text-center text-xl">
              Estimated Life Expectancy (EL)
            </label>
            <div className="bg-gradient-to-r from-green-500/30 to-teal-500/30 backdrop-blur-lg rounded-xl p-6 text-center border-2 border-green-400/30 mb-6" data-testid="estimated-life-result">
              {estimatedLifeExpectancy !== null ? (
                <>
                  <div className="text-4xl md:text-5xl font-bold text-white mb-2">
                    {estimatedLifeExpectancy.toLocaleString()}
                  </div>
                  <div className="text-lg text-green-200">hours (estimated life)</div>
                </>
              ) : (
                <div className="text-xl text-white/60">
                  Select RL and DC to calculate
                </div>
              )}
            </div>

            {/* Formula Display for EL */}
            <div className="text-center mb-8">
              <p className="text-sm text-green-200">
                Formula: <span className="font-mono bg-white/10 px-3 py-1 rounded">EL = (RL × DC) / 100</span>
              </p>
              <p className="text-xs text-blue-200/70 mt-2">
                Where RL = Rated Life, DC = Duty Cycle percentage
              </p>
            </div>
          </div>

          {/* Remaining Life Result */}
          <div className="pt-8 border-t border-white/20">
            <label className="block text-white font-semibold mb-3 text-center text-xl">
              Remaining Life in Hours (RLH)
            </label>
            <div className="bg-gradient-to-r from-blue-500/30 to-purple-500/30 backdrop-blur-lg rounded-xl p-6 text-center border-2 border-white/30" data-testid="remaining-life-result">
              {remainingLife !== null ? (
                <>
                  <div className="text-5xl md:text-6xl font-bold text-white mb-2">
                    {remainingLife >= 0 ? remainingLife.toLocaleString() : 0}
                  </div>
                  <div className="text-xl text-blue-200">hours remaining</div>
                  {remainingLife < 0 && (
                    <div className="mt-4 text-red-300 font-semibold">
                      ⚠️ Warning: Motor has exceeded estimated life expectancy by {Math.abs(remainingLife).toLocaleString()} hours
                    </div>
                  )}
                  {remainingLife >= 0 && remainingLife < 5000 && (
                    <div className="mt-4 text-yellow-300 font-semibold">
                      ⚠️ Caution: Motor approaching end of estimated life
                    </div>
                  )}
                </>
              ) : (
                <div className="text-2xl text-white/60">
                  Select all values above to calculate
                </div>
              )}
            </div>

            {/* Formula Display for RLH */}
            <div className="mt-6 text-center">
              <p className="text-sm text-blue-200">
                Formula: <span className="font-mono bg-white/10 px-3 py-1 rounded">RLH = EL - AH</span>
              </p>
              <p className="text-xs text-blue-200/70 mt-2">
                Where EL = Estimated Life Expectancy, AH = Actual Hours
              </p>
            </div>
          </div>

          {/* Info Section */}
          <div className="mt-8 bg-blue-500/10 border border-blue-400/30 rounded-lg p-6">
            <h3 className="text-white font-bold text-lg mb-3">📋 How to Use</h3>
            <ul className="text-blue-100 space-y-2 text-sm">
              <li>• <strong>Motor Brand:</strong> Select the manufacturer of your motor</li>
              <li>• <strong>Motor Size:</strong> Choose the horsepower rating</li>
              <li>• <strong>Duty Cycle (DC):</strong> Percentage of time motor runs under load (used for EL calculation)</li>
              <li>• <strong>Operating Time:</strong> Average daily operation hours</li>
              <li>• <strong>Rated Life (RL):</strong> Manufacturer's rated lifespan (used for EL calculation)</li>
              <li>• <strong>Actual Hours (AH):</strong> Current operating hours on the motor</li>
            </ul>
            
            <div className="mt-4 pt-4 border-t border-blue-400/30">
              <h4 className="text-white font-bold mb-2">📐 Calculations</h4>
              <ul className="text-blue-100 space-y-1 text-sm">
                <li>• <strong>Estimated Life (EL):</strong> EL = (RL × DC) / 100</li>
                <li>• <strong>Remaining Life (RLH):</strong> RLH = EL - AH</li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default MotorLifeCalculator;
